import type { WebApp, WebAppUser } from './types';
import { TelegramUnavailableError } from './types';

export function getWebApp(): WebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function requireWebApp(): WebApp {
  const webApp = getWebApp();
  if (!webApp) throw new TelegramUnavailableError();
  return webApp;
}

export function isTelegramEnvironment(): boolean {
  return Boolean(getWebApp()?.initData);
}

/**
 * Сырая строка initData — её проверяет бэкенд (`POST /auth/telegram`).
 * Менять или пересобирать строку нельзя: подпись HMAC считается по ней как есть.
 */
export function getInitData(): string {
  const initData = requireWebApp().initData;
  if (!initData) {
    throw new TelegramUnavailableError(
      'Telegram не передал initData — вход невозможен',
    );
  }
  return initData;
}

/** Данные игрока из initDataUnsafe. Только для UI: доверять им нельзя. */
export function getUnsafeUser(): WebAppUser | null {
  return getWebApp()?.initDataUnsafe.user ?? null;
}

/**
 * Клиент, из которого открыт мини-апп: `android`, `ios`, `tdesktop`,
 * `macos`, `weba`, `android_x`.
 *
 * Это ответ САМОГО Telegram, а не догадка по User-Agent, и в аналитике
 * он полезнее «операционной системы». Метрика видит только WebView,
 * поэтому Telegram Desktop в её отчётах неотличим от обычного браузера
 * на том же Windows — а различие практическое: другой размер экрана,
 * нет безопасных зон, иначе ведёт себя клавиатура.
 *
 * `null` — приложение открыто вне Telegram (демо-режим, e2e).
 */
export function getPlatform(): string | null {
  return getWebApp()?.platform ?? null;
}

/**
 * Версия клиента Telegram, например `7.10`.
 *
 * По ней видно тех, у кого старый клиент: часть возможностей мини-аппа
 * там просто отсутствует, и жалоба «не работает кнопка» объясняется
 * версией, а не поломкой.
 */
export function getClientVersion(): string | null {
  return getWebApp()?.version ?? null;
}

/** Параметр ссылки `?startapp=` — реферальный код пригласившего. */
export function getStartParam(): string | null {
  return getWebApp()?.initDataUnsafe.start_param ?? null;
}

/**
 * Разворачивает мини-апп и выключает свайп-закрытие: иначе вертикальный скролл
 * ленты матчей закрывает приложение. Оба вызова есть не во всех версиях клиента.
 */
export function initWebApp(): void {
  const webApp = getWebApp();
  if (!webApp) return;

  webApp.ready();
  webApp.expand();

  if (webApp.isVersionAtLeast('7.7')) {
    webApp.disableVerticalSwipes();
  }
}

export function hapticImpact(
  style: 'light' | 'medium' | 'heavy' = 'light',
): void {
  getWebApp()?.HapticFeedback.impactOccurred(style);
}

export function hapticNotification(
  type: 'error' | 'success' | 'warning',
): void {
  getWebApp()?.HapticFeedback.notificationOccurred(type);
}

/** Системная кнопка «Назад». Возвращает функцию отписки. */
export function bindBackButton(handler: () => void): () => void {
  const webApp = getWebApp();
  if (!webApp) return () => undefined;

  webApp.BackButton.onClick(handler);
  webApp.BackButton.show();

  return () => {
    webApp.BackButton.offClick(handler);
    webApp.BackButton.hide();
  };
}

/**
 * Возврат в мини-апп. Возвращает функцию отписки.
 *
 * Игрок сворачивает Telegram и возвращается через час — это норма, а не
 * край: к этому моменту баланс, отсечки и список матчей уже протухли.
 *
 * Событие `activated` есть только с версии 8.0, поэтому вторым каналом идёт
 * `visibilitychange`: в вебе и на старых клиентах работает только он.
 */
export function bindAppResume(handler: () => void): () => void {
  const webApp = getWebApp();

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') handler();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  const supportsActivated = webApp?.isVersionAtLeast('8.0') ?? false;
  if (webApp && supportsActivated) {
    webApp.onEvent('activated', handler);
  }

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (webApp && supportsActivated) {
      webApp.offEvent('activated', handler);
    }
  };
}

/** Внешние ссылки открываются только через SDK, не через window.open. */
export function openLink(url: string): void {
  const webApp = getWebApp();
  if (webApp) {
    if (url.startsWith('https://t.me/')) {
      webApp.openTelegramLink(url);
    } else {
      webApp.openLink(url);
    }
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
